import { isNumber } from "lodash";
import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LoadResponse } from "../../../../../models/api/load";
import { DTOModel } from "../../../../../models/base/dto-model";
import { EmployeeGroupDTO } from "../../../../../models/employee-groups";
import { TnADayDataRef } from "../../../../../models/tna/tna-data/tna-day-data-ref";
import { TnAWorkedTimeDTO } from "../../../../../models/tna-worked-time";
import { exportTnAToExcel } from "../../../../../utils/export-presence-and-leaves";
import {
  getDayAndMonth,
  getDaysInMonth,
  getNormaleDateOrMonth,
  intlFormatDateShort,
  intlFormatDateWithHour,
} from "../../../../../utils/time-utils";
import { monthsValues } from "../../../../CustomDropdowns/CustomDropdownMonthsValues/CustomDropdownMonthsValues";
import ExportToExcelDialog from "../../../ExportToExcelDialog/ExportToExcelDialog";
import { MyTnAContext } from "../../../MyTeamPresenceAndLeaves";
import { excelStyles } from "../../../utils";

interface Props {
  disabled: boolean;
  loadEmployeeGroup: () => Promise<EmployeeGroupDTO | undefined>;
  loadTnAWorkedTimeData: (pageSize?: number) => Promise<LoadResponse<TnAWorkedTimeDTO> | undefined>;
  allItems: () => DTOModel[];
}

const ExportWorkedTimeTableView = ({ disabled, loadEmployeeGroup, loadTnAWorkedTimeData, allItems }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_worked_time";
  const translationFields = `${tRP}.fields`;
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity },
    employeeGroup: { employeeGroupOption },
    currentEmployees: { onlyCurrentEmployeesOptionValue },
    searchingDate: { year, month },
  } = useContext(MyTnAContext);

  const rowsBeforeHeaderInExcel = useCallback(
    (employeeGroup: EmployeeGroupDTO | undefined) => {
      return [
        {
          data: ["Worked time"],
          fontSize: 16,
          isBold: true,
        },
        {
          data: [`Report date ${intlFormatDateWithHour(new Date())}`],
          fontSize: 14,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Legal entities: ${!legalEntityOption?.legalEntityIds?.length ? "All" : selectedLegalEntity?.name ?? ""}`,
          ],
          fontSize: 16,
          isBold: true,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Employee group: ${
              !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
            }`,
          ],
          fontSize: 12,
          isBold: false,
        },
        {
          data: [
            `Employee status: ${
              onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees ? "Only current employees" : "All employees"
            }`,
          ],
          fontSize: 12,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [`Period: ${year} ${t(monthsValues.find((currMonth) => currMonth.value === month)?.key ?? "")}`],
          fontSize: 14,
          isBold: true,
        },
      ];
    },
    [
      legalEntityOption?.legalEntityIds,
      selectedLegalEntity?.name,
      employeeGroupOption,
      onlyCurrentEmployeesOptionValue,
      year,
      month,
    ]
  );

  const headerNames = useMemo(() => {
    const daysInMonth = getDaysInMonth(year, month);

    const dates: string[] = [];

    for (let i = 0; i < daysInMonth; i++) {
      dates.push(getDayAndMonth(new Date(year, month, i + 1)));
    }

    return [
      `${t(`${tRPCommon}.employee_code`)}`,
      `${t(`${tRPCommon}.internal_id`)}`,
      `${t("common_words.employee")}`,
      `${t(`${tRPCommon}.birth_date`)}`,
      `${t("common_words.legal_entity")}`,
      `${t("common_words.tna_employee_group")}`,
      `${t(`${translationFields}.total_worked_this_month`)}`,
      `${t(`${translationFields}.current_due_vs_worked_time`)}`,
      `${t(`${translationFields}.total_due_vs_worked_balance`)}`,
      ...dates,
    ];
  }, [year, month]);

  const dataToExportAsExcel = useCallback(
    (allItems: TnAWorkedTimeDTO[]) => {
      const daysInMonth = getDaysInMonth(year, month);

      return allItems.map((item) => {
        const dates: { [key: string]: { value: string; style?: any } } = {};

        for (let i = 0; i < daysInMonth; i++) {
          const currDay = item?.daysData?.find(
            (day: TnADayDataRef) =>
              day?.tnADate?.day === i + 1 && day.tnADate.month === month + 1 && day.tnADate.year === year
          );

          const isWeekend = [0, 6].indexOf(new Date(year, month, i + 1).getDay()) != -1;
          const hasAnomalies = currDay
            ? currDay.anomalies?.length > 0 || currDay.abnormalTime || currDay.hasAnomaly
            : false;
          // console.log(hasAnomalies);

          if (!isWeekend && currDay?.absences?.length && currDay?.absences?.length > 0) {
            const absenceTexts = currDay?.absences.map((item) => {
              const absenceStatus = `${item.absenceType?.code}\n${
                item.isCancelled
                  ? `(Cancelled)`
                  : item?.isApproved
                  ? `Accepted by ${item?.modifiedByUser?.name}`
                  : item?.isRejected
                  ? `Rejected by ${item?.modifiedByUser?.name}`
                  : ""
              } ${
                !item.isCancelled &&
                item.cancelRequested &&
                item.cancelRequestedAt &&
                item.cancellationRequestApproved === null
                  ? ` \n (Cancel requested)`
                  : ""
              }`;
              // absence.isCancelled
              //   ? t(`${tRP}.export_fields.statuses.cancelled`)
              //   : absence.isApproved
              //   ? t(`${tRP}.export_fields.statuses.approved`)
              //   : absence.isRejected
              //   ? t(`${tRP}.export_fields.statuses.rejected`)
              //   : t(`${tRP}.export_fields.statuses.pending`);
              // const managerName = absence.modifiedByUser?.name || "";
              // const clientStatus = absence.needAcceptance
              //   ? absence.acceptedByClient
              //     ? t(`${tRP}.export_fields.accepted_by_client`)
              //     : absence.rejectedByClient
              //     ? t(`${tRP}.export_fields.rejected_by_client`)
              //     : ""
              //   : "";

              // let absenceText = `${absence.absenceType?.name || ""} (${absence.calendaristicDays})`;
              // if (managerName) {
              //   absenceText += ` / ${absenceStatus} ${t(`${tRP}.export_fields.by`)} ${managerName}`;
              // }
              // if (clientStatus) {
              //   absenceText += ` / ${clientStatus} ${absence.acceptedRejectedByUser?.name || ""}`;
              // }
              // if (!managerName && !clientStatus) {
              //   absenceText += ` / ${absenceStatus}`;
              // }
              return absenceStatus;
            });

            let combinedText = absenceTexts.join("\n");

            const workedTime = currDay?.expectedPresence || currDay?.presence > 0 ? currDay?.presence : null;
            const anomalyText = hasAnomalies
              ? `${t(`common_words.anomalies`)}:\n ${currDay.anomalies?.map(
                  (_anomaly) => `${_anomaly.anomalyType?.name} \n`
                )} `
              : "";

            if (workedTime) {
              combinedText += `\n${t(`${tRP}.export_fields.worked_time`)}: ${
                isNumber(workedTime) ? workedTime.toFixed(2) : ""
              } ${anomalyText}`;

              // combinedText += `\n${t(`${tRP}.export_fields.worked_time`)}: ${
              //   isNumber(workedTime) ? workedTime.toFixed(2) : ""
              // } ${currDay?.abnormalTime ? `\n${currDay?.abnormalTimeDescription ?? ""}` : ""} ${
              //   currDay?.hasAnomaly ? `\n${currDay?.anomalyDescription ?? ""}` : ""
              // }`;
            }

            dates[getDayAndMonth(new Date(year, month, i + 1))] = {
              value: combinedText,
              style: {
                backgroundColor: isWeekend
                  ? excelStyles.weekendCell.backgroundColor
                  : currDay?.isPublicHoliday
                  ? excelStyles.publicHolidayCellStyle.backgroundColor
                  : excelStyles.normalCell.backgroundColor,
                fontSize: hasAnomalies ? 9 : 11,
                horizontalAlign: hasAnomalies ? "left" : "center",
              },
            };
          } else if (currDay) {
            const workedTime = currDay.expectedPresence > 0 || currDay.presence > 0 ? currDay.presence : null;
            const anomalyText = hasAnomalies
              ? `${t(`common_words.anomalies`)}:\n ${currDay.anomalies?.map(
                  (_anomaly) => `${_anomaly.anomalyType?.name} \n`
                )}`
              : "";

            dates[getDayAndMonth(new Date(year, month, i + 1))] = {
              value: `${isNumber(workedTime) ? workedTime.toFixed(2) : ""}  ${anomalyText}`,
              style: {
                backgroundColor: isWeekend
                  ? excelStyles.weekendCell.backgroundColor
                  : currDay?.isPublicHoliday
                  ? excelStyles.publicHolidayCellStyle.backgroundColor
                  : excelStyles.normalCell.backgroundColor,
                fontSize: hasAnomalies ? 10 : 11,
                horizontalAlign: hasAnomalies ? "left" : "center",
              },
            };
          } else {
            dates[getDayAndMonth(new Date(year, month, i + 1))] = { value: "" };
          }
        }

        // console.log(dates)

        return {
          [`${t(`${tRPCommon}.employee_code`)}`]: { value: item.employee?.employeeCode ?? " " },
          [`${t(`${tRPCommon}.internal_id`)}`]: { value: item.employee?.internalId ?? " " },
          [`${t("common_words.employee")}`]: {
            value: item.employee?.name ?? " ",
            style: { ...excelStyles.employeeCellStyle },
          },
          [`${t(`${tRPCommon}.birth_date`)}`]: {
            value: item.employee?.birthDate ? intlFormatDateShort(item.employee?.birthDate) : " ",
          },
          [`${t("common_words.legal_entity")}`]: { value: item.employee?.legalEntity?.name ?? " " },
          [`${t("common_words.tna_employee_group")}`]: { value: item?.employee?.tnAGroup?.name ?? " " },
          [`${t(`${translationFields}.total_worked_this_month`)}`]: {
            value: `${item?.workedTime ? item?.workedTime.toFixed(2) : " "}`,
          },
          [`${t(`${translationFields}.current_due_vs_worked_time`)}`]: {
            value: `${item?.workedTimeBalance ? item?.workedTimeBalance.toFixed(2) : " "}`,
          },
          [`${t(`${translationFields}.total_due_vs_worked_balance`)}`]: {
            value: `${item?.totalWorkedTimeBalance ? item?.totalWorkedTimeBalance.toFixed(2) : " "}`,
          },
          ...dates,
          sortDirection: headerNames,
        };
      });
    },
    [headerNames, year, month]
  );

  const exportToExcel = useCallback(
    async (option: "exportAllData" | "exportCurrent" = "exportCurrent") => {
      if (option === "exportCurrent") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;
        exportTnAToExcel(
          dataToExportAsExcel(allItems() as TnAWorkedTimeDTO[]),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } Worked time`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }

      if (option === "exportAllData") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        const firstResponse = await loadTnAWorkedTimeData();

        if (firstResponse?.error) return;

        const allTnAWorkedTimeData = await loadTnAWorkedTimeData(firstResponse?.totalItems);

        if (!!allTnAWorkedTimeData?.error || !allTnAWorkedTimeData?.items) return;

        exportTnAToExcel(
          dataToExportAsExcel(allTnAWorkedTimeData.items),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } Worked time`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }
    },
    [
      rowsBeforeHeaderInExcel,
      loadEmployeeGroup,
      headerNames,
      dataToExportAsExcel,
      employeeGroupOption?.employeeGroupIds,
      loadTnAWorkedTimeData,
    ]
  );

  return (
    <ExportToExcelDialog
      handleExportToExcel={exportToExcel}
      disabled={disabled}
    />
  );
};

export default ExportWorkedTimeTableView;
